import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { GridFour, Check, Lock, Wrench, Timer, Star } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 3D-look styling per status: gradient + ring + inner highlight + glow + icon
const STATUS_THEMES = {
  available: {
    bg: 'bg-gradient-to-br from-emerald-300 via-emerald-500 to-emerald-700',
    ring: 'ring-emerald-200/60',
    shadow: 'shadow-[0_4px_0_-1px_rgba(4,120,87,0.45),0_8px_18px_-6px_rgba(16,185,129,0.55)]',
    legendBg: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
    label: 'Available',
    icon: Check,
  },
  occupied: {
    bg: 'bg-gradient-to-br from-rose-300 via-rose-500 to-rose-700',
    ring: 'ring-rose-200/50',
    shadow: 'shadow-[0_4px_0_-1px_rgba(159,18,57,0.45),0_8px_18px_-6px_rgba(244,63,94,0.5)]',
    legendBg: 'bg-gradient-to-br from-rose-400 to-rose-600',
    label: 'Occupied',
    icon: Lock,
  },
  reserved: {
    bg: 'bg-gradient-to-br from-amber-300 via-amber-500 to-amber-700',
    ring: 'ring-amber-200/50',
    shadow: 'shadow-[0_4px_0_-1px_rgba(146,64,14,0.4),0_8px_18px_-6px_rgba(245,158,11,0.5)]',
    legendBg: 'bg-gradient-to-br from-amber-400 to-amber-600',
    label: 'Reserved',
    icon: Timer,
  },
  maintenance: {
    bg: 'bg-gradient-to-br from-slate-200 via-slate-400 to-slate-600',
    ring: 'ring-slate-200/50',
    shadow: 'shadow-[0_4px_0_-1px_rgba(51,65,85,0.35),0_8px_18px_-6px_rgba(100,116,139,0.5)]',
    legendBg: 'bg-gradient-to-br from-slate-300 to-slate-500',
    label: 'Maintenance',
    icon: Wrench,
  },
};

const SpaceMap = ({ listingId, onUnitSelect, selectedUnitId, interactive = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/spaces/${listingId}`);
        if (res.data.units?.length > 0) setData(res.data);
        else setData(null);
      } catch { setData(null); }
      finally { setLoading(false); }
    };
    fetchUnits();
  }, [listingId]);

  const sortedRows = useMemo(() => {
    const rows = {};
    (data?.units || []).forEach(u => { if (!rows[u.row]) rows[u.row] = []; rows[u.row].push(u); });
    return Object.entries(rows)
      .sort(([a], [b]) => a - b)
      .map(([rowIdx, rowUnits]) => [rowIdx, [...rowUnits].sort((a, b) => a.col - b.col)]);
  }, [data]);

  if (loading) return null;
  if (!data || !data.units?.length) return null;

  const { summary, grid } = data;

  const handleUnitClick = (unit) => {
    if (unit.status === 'available' && interactive && onUnitSelect) {
      onUnitSelect(unit);
    }
  };

  return (
    <div data-testid="space-map-section">
      <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <span className="relative inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-300 to-emerald-600 shadow-[0_3px_8px_-2px_rgba(16,185,129,0.6)] ring-1 ring-white/40">
          <span className="absolute inset-0 rounded-lg bg-gradient-to-b from-white/35 to-transparent pointer-events-none" />
          <GridFour size={16} className="text-white relative z-10" weight="bold" />
        </span>
        Space Availability
      </h3>

      {interactive && (
        <p className="text-sm text-emerald-700 bg-gradient-to-r from-emerald-50 via-emerald-50 to-emerald-100/70 border border-emerald-200/60 rounded-xl px-3.5 py-2.5 mb-4 font-medium flex items-center gap-2 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.7)]" data-testid="click-to-book-hint">
          <Star size={15} weight="fill" className="text-emerald-500" />
          Click on a green slot to select it for booking
        </p>
      )}

      {/* Summary Legend — premium 3D chips */}
      <div className="flex gap-2.5 mb-5 flex-wrap" data-testid="space-map-legend">
        {Object.entries(STATUS_THEMES).map(([status, cfg]) => {
          const Icon = cfg.icon;
          return (
            <div key={status} className="flex items-center gap-2 bg-white border border-slate-200/80 rounded-full pl-1 pr-3 py-1 shadow-[0_1px_2px_0_rgba(0,0,0,0.04),0_2px_4px_-2px_rgba(0,0,0,0.05)]">
              <span className={`relative w-6 h-6 rounded-full ${cfg.legendBg} flex items-center justify-center shadow-inner ring-1 ring-white/30`}>
                <span className="absolute inset-0 rounded-full bg-gradient-to-b from-white/30 to-transparent" />
                <Icon size={11} className="text-white relative z-10" weight="bold" />
              </span>
              <span className="text-xs text-slate-600 font-medium">
                {cfg.label}: <strong className="text-slate-900 tabular-nums">{summary[status] || 0}</strong>
              </span>
            </div>
          );
        })}
      </div>

      {/* 3D Grid */}
      <div className="bg-gradient-to-b from-slate-50 to-slate-100 rounded-2xl p-5 overflow-x-auto border border-slate-200/70 shadow-inner" data-testid="space-map-grid">
        <div className="inline-block min-w-fit">
          {sortedRows.map(([rowIdx, rowUnits]) => (
            <div key={rowIdx} className="flex items-center gap-2 mb-2 last:mb-0">
              <div className="w-6 text-[11px] font-bold text-slate-400 text-center tracking-wider">
                {String.fromCharCode(65 + parseInt(rowIdx))}
              </div>
              {rowUnits.map(unit => {
                const cfg = STATUS_THEMES[unit.status] || STATUS_THEMES.available;
                const isAvailable = unit.status === 'available';
                const isSelected = selectedUnitId === unit.unit_id;
                const clickable = interactive && isAvailable;

                return (
                  <button
                    key={unit.unit_id}
                    onClick={() => handleUnitClick(unit)}
                    disabled={!clickable}
                    className={`group relative w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-150 overflow-hidden
                      ${isSelected
                        ? 'bg-gradient-to-br from-blue-300 via-blue-500 to-blue-700 ring-2 ring-blue-400/70 ring-offset-2 ring-offset-white scale-105 shadow-[0_6px_0_-1px_rgba(30,64,175,0.5),0_12px_24px_-6px_rgba(59,130,246,0.55)]'
                        : `${cfg.bg} ${cfg.shadow} ring-1 ${cfg.ring}`
                      }
                      ${clickable && !isSelected ? 'hover:-translate-y-0.5 hover:shadow-[0_8px_0_-1px_rgba(4,120,87,0.5),0_12px_24px_-6px_rgba(16,185,129,0.6)] active:translate-y-0 active:scale-95 cursor-pointer' : 'cursor-default'}
                    `}
                    title={`${unit.label} - ${isSelected ? 'Selected' : cfg.label}${clickable ? ' (Click to book)' : ''}`}
                    data-testid={`buyer-unit-${unit.unit_id}`}
                  >
                    {/* Top glassy highlight */}
                    <span className="absolute inset-x-1 top-1 h-1/2 rounded-t-xl bg-gradient-to-b from-white/35 to-transparent pointer-events-none" />
                    {/* Bottom subtle inner shadow */}
                    <span className="absolute inset-x-1.5 bottom-0.5 h-1 rounded-b-md bg-black/15 blur-[2px] pointer-events-none" />
                    <span className="relative z-10 text-[11px] font-extrabold text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)] tracking-wide">
                      {unit.label}
                    </span>
                    {isSelected && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white text-blue-600 text-[9px] font-black flex items-center justify-center shadow ring-1 ring-blue-200">
                        <Check size={9} weight="bold" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-slate-500 mt-3 font-medium" data-testid="space-map-summary-text">
        <span className="text-emerald-600 font-bold tabular-nums">{summary.available}</span> of <span className="font-bold tabular-nums">{summary.total}</span> {grid.unit_type || 'unit'}s available
      </p>
    </div>
  );
};

export default SpaceMap;
